import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

// Manual record interface
export interface ManualRecordRequest {
    qpID: string;
    userID: string;
    timestamp: number;
    type: string;
}

/** The two photos behind a kiosk fichada. Both URLs are optional — see getKioskRecordEvidence. */
export interface KioskRecordEvidence {
    recordID: string;
    type: string;
    timestamp: number;
    evidenceImageURL?: string;
    referenceImageURL?: string;
    evidenceStoredAt?: number;
    /** the reference photo is the collaborator's current enrollment, not necessarily the one that matched */
    referenceIsCurrentEnrollment: boolean;
}

export class DashboardService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getBuildingsWithQps(orgId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getBuildingsWithQps, { org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getBuildingsWithQpsPaginated(orgId: string, lastKey: string = "", pageSize: string = "50") {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getBuildingsWithQpsPaginated, { org_id: orgId, lastKey, pageSize });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getUserCount(orgId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getUserCount, { org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getPresenteeismTableRange(orgId: string, startDate: string, endDate: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getPresenteeismTableRange, { org_id: orgId, startDate, endDate });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getPresenteeismTableDay(orgId: string, processedDate: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getPresenteeismTableDay, { org_id: orgId, processedDate });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    /**
     * The two photos behind a kiosk fichada. Either URL can be absent (no photo stored,
     * collaborator not enrolled, record predates the feature) and a present URL can still
     * 404, because presigning does not check that the object still exists — the 90-day
     * lifecycle rule deletes evidence out from under the record. Render each independently.
     */
    async getKioskRecordEvidence(recordID: string): Promise<KioskRecordEvidence> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getKioskRecordEvidence, { record_id: recordID });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result as KioskRecordEvidence;
    }

    async getPresenteeismShiftsDay(orgId: string, processedDate: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getPresenteeismShiftsDay, { org_id: orgId, processedDate });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async createManualRecord(recordData: ManualRecordRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.postManualRecord, {});
        request.method = "POST";
        request.body = recordData;
        return this.httpClient.execute(request);
    }

    async getOrganizationsPaginated(uid: string, pageSize: string, lastKey: string, useCache: boolean = true) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getOrganizationsPaginated, { uid, pageSize, lastKey });
        request.method = "GET";
        if (useCache) {
            request.cache = {
                enable: true,
                ttlMs: 600000
            };
        }
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getUserRoleInOrganization(uid: string, orgId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Dashboard.getUserRoleInOrganization, { user_id: uid, org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }
}
